/**
 * PlansTab
 *
 * Patient-scoped plans tab extracted from PatientDetailPage. Mirrors the legacy
 * plan_details-test.php layout including plan details and plan-scoped tasks.
 *
 * Sections (top to bottom):
 *  1. Section header with "Plans" title + Add Plan button
 *  2. Plan list table (all plans for the patient, click-to-select)
 *  3. Plan header bar with Plan Type dropdown
 *  4. Full-width stage pipeline (PlanProgressBar with stretch)
 *  5. Status dropdown (PlanStatusSelect)
 *  6. Editable plan details form (two-column layout)
 *  7. Plan-scoped tasks section with filter tabs
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, Bell, Calendar, ChevronDown, ChevronUp, ClipboardList, CreditCard, FileText, History, Loader2, Mail, MessageSquare, Package, Pencil, Phone, Plus, ScrollText, ShoppingBag } from 'lucide-react';
import { Badge, Button, Input, Modal, Select, Spinner, Textarea } from '@/components/ui';
import { parseDateValue } from '@/lib/utils';
import { PatientSearchInput } from '@/components/medical/PatientSearchInput';
import { PlanProgressBar } from '@/pages/plans/PlanProgressBar';
import { PlanStatusSelect } from '@/pages/plans/PlanStatusSelect';
import { TaskModal } from '@/pages/TasksPage';
import { taskStatusLabel as resolveTaskStatusLabel } from '@/pages/tasks/tasksShared';
import { checkPlanType, getPlansByPatient, getPlan, getPlanProgressBar, getPlanStageSubs, updatePlan, getPlanHistory, getPlanAssociatedEntities } from '@/services/plans.service';
import type { Plan, PlanListItem, UpdatePlanPayload, PlanHistoryEntry, PlanAssociatedAppointment, PlanAssociatedMembership, PlanAssociatedPackage } from '@/services/plans.service';
import { appointmentsService, type Appointment } from '@/services/appointments.service';
import { AppointmentDetailModal } from '@/pages/appointments/AppointmentDetailModal';
import { AppointmentCreateModal } from '@/pages/appointments/AppointmentCreateModal';
import { invalidatePatientAppointments } from '@/hooks/usePatientDetail';
import { ACTION_BUTTON_CLASS, PlanPaymentsSection, PlanQuotesSection } from '@/pages/patients/PlanFinanceSections';
import { RecurringDetailModal } from '@/pages/memberships/RecurringDetailModal';
import { PackageDetailModal } from '@/pages/patients/PackageDetailModal';
import { getPatientProductTypes } from '@/services/patientProductTypes.service';
import { ProductGroupCard, type ProductTypesResponse, type ProductGroup } from '@/components/patients/PatientProductsDisplay';
import { getCommLogByPlan } from '@/services/communications.service';
import type { CommLogEntry, CommLogType } from '@/services/communications.service';
import { listDoctors, formatDoctorName } from '@/services/doctors.service';
import { listCoordinators, formatCoordinatorName } from '@/services/coordinators.service';
import { listNurses, formatNurseName } from '@/services/nurses.service';
import { listFacilities, listLeadSourceCategories, listPlanTypes, listTimeframes, listUsers } from '@/services/admin.service';
import type { AdminUser, Facility, LeadSourceCategory, Timeframe } from '@/services/admin.service';
import { COMMITMENT_LEVEL_OPTIONS, getCommitmentLevelLabel } from '@/services/leadIntake.service';
import {
  listTasks,
  updateTask,
  getTaskCounts,
  getTaskFilterTab,
  saveTaskFilterTab,
  type Task,
  type TaskCounts,
} from '@/services/tasks.service';
import { TaskDoneButton } from '@/components/tasks/TaskDoneButton';
import { useRoleTitles } from '@/hooks/useRoleTitles';
import { MY_TASK_COUNTS_KEY } from '@/hooks/useMyTaskCounts';
import { useUserTimezone } from '@/hooks/useUserTimezone';
import { pacificToWallClock, getTimezoneAbbreviation } from '@/lib/facilityTimezone';
import { buildStaffOptions, staffQueryKey, toIncludeIds } from '@/lib/staffOptions';
import { toast } from '@/store/toastStore';

// ─── Constants ────────────────────────────────────────────────────────────────

const LEAD_TYPE_OPTIONS: { value: number; label: string }[] = [
  { value: 1, label: 'Phone Call' },
  { value: 2, label: 'Event' },
  { value: 3, label: 'Web Lead' },
  { value: 4, label: 'Walk-in' },
  { value: 5, label: 'Returning Patient' },
  { value: 6, label: 'Other' },
  { value: 7, label: 'Social Media' },
  { value: 8, label: 'Chat' },
  { value: 9, label: 'Formstack' },
  { value: 14, label: 'SMS' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function DetailCell({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="min-w-0">
      <span className="text-[11px] font-medium text-slate-400">{label}</span>
      <p className="truncate text-xs text-slate-700">{value || '—'}</p>
    </div>
  );
}

/** Turn bare URLs in an HTML string into clickable <a> links, skipping URLs already inside anchor tags. */
function linkifyHtml(html: string): string {
  const urlRe = /(https?:\/\/[^\s<>"']+)/gi;
  const parts: string[] = [];
  let cursor = 0;
  const tagRe = /<a[\s>][\s\S]*?<\/a>/gi;
  const anchors = new Map<number, number>();
  let m: RegExpExecArray | null;
  while ((m = tagRe.exec(html)) !== null) {
    anchors.set(m.index, m.index + m[0].length);
  }
  html.replace(urlRe, (match, _g1, offset) => {
    for (const [start, end] of anchors) {
      if (offset >= start && offset < end) return match;
    }
    parts.push(html.slice(cursor, offset));
    parts.push(`<a href="${match}" target="_blank" rel="noopener noreferrer" class="text-indigo-600 underline break-all">${match}</a>`);
    cursor = offset + match.length;
    return match;
  });
  parts.push(html.slice(cursor));
  return parts.join('');
}

function parsePacificDatetime(value: string): { date: string; time: string } | null {
  const match = value.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/);
  if (!match) return null;
  return { date: match[1], time: match[2] };
}

function formatDateTimeTz(value: string | null | undefined, tz: string): { date: string; time: string } {
  if (!value) return { date: '—', time: '' };

  const parsed = parsePacificDatetime(value);
  if (!parsed) return { date: '—', time: '' };

  const wall = pacificToWallClock(parsed.date, parsed.time, tz);

  const [y, mo, day] = wall.date.split('-').map(Number);
  const [h, mi] = wall.time.split(':').map(Number);
  const dateObj = new Date(y, mo - 1, day, h, mi);
  const datePart = dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const timePart = dateObj.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  const tzAbbr = getTimezoneAbbreviation(tz, dateObj);
  return { date: datePart, time: `${timePart} ${tzAbbr}` };
}

function getPlanStageBadgeVariant(stageName: string | null): 'success' | 'warning' | 'info' | 'neutral' {
  if (!stageName) return 'neutral';
  const lower = stageName.toLowerCase();
  if (lower.includes('inquiry') || lower.includes('lead') || lower.includes('consult')) return 'info';
  if (lower.includes('surg') || lower.includes('schedul') || lower.includes('proced')) return 'warning';
  if (lower.includes('complete') || lower.includes('closed') || lower.includes('won')) return 'success';
  return 'neutral';
}

// ─── Task Helpers ─────────────────────────────────────────────────────────────

const TASK_FILTER_TABS = [
  { flTasks: 4, label: 'Past Due',    countKey: 'pastDue'   as const, showCount: true },
  { flTasks: 3, label: 'Due Today',   countKey: 'dueToday'  as const, showCount: true },
  { flTasks: 2, label: 'Upcoming',    countKey: 'upcoming'  as const, showCount: true },
  { flTasks: 5, label: 'Complete',    countKey: 'completed' as const, showCount: false },
  { flTasks: 6, label: 'Inactive',    countKey: 'inactive'  as const, showCount: false },
  { flTasks: 1, label: 'All Entries', countKey: 'all'       as const, showCount: false },
];

const TASK_STATUS_CLASS: Record<string, string> = {
  'Past Due':  'text-red-600 font-semibold',
  'Due':       'text-orange-600 font-semibold',
  'Upcoming':  'text-blue-600',
  'Completed': 'text-slate-500',
  'Inactive':  'text-slate-400',
};

/**
 * The label comes from the one shared resolver so this tab can never disagree
 * with the badge, the Done gate, or the task modal's read-only lock. The local
 * fallback this replaces read a '0000-00-00' due date as a real date and called
 * those tasks 'Past Due' (and a missing one 'Upcoming'), where the server calls
 * both Inactive — a task with no due date could therefore be offered a Done
 * button here. Only the styling stays local.
 */
function deriveTaskStatus(task: Task): { label: string; className: string } {
  const label = resolveTaskStatusLabel(task);
  return { label, className: TASK_STATUS_CLASS[label] ?? 'text-slate-500' };
}

function priorityLabel(p: number | string | undefined | null): string {
  if (typeof p === 'string') return p;
  if (p === 1) return 'Low';
  if (p === 2) return 'Medium';
  if (p === 3) return 'High';
  return 'Normal';
}

function formatShortDate(value: string | null | undefined): string {
  // parseDateValue treats a date-only "YYYY-MM-DD" as local midnight, avoiding
  // the UTC-parse off-by-one that rendered task dates a day early.
  const parsed = parseDateValue(value);
  if (!parsed) return '—';
  return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ─── PlansTab ─────────────────────────────────────────────────────────────────

interface PlansTabProps {
  patientId: number;
  initialPlanId?: number;
  /** The patient's Quotes tab is visible; gates the plan's Quotes card. */
  showQuotes?: boolean;
  /** The patient's Payments tab is visible; gates the plan's Payments card. */
  showPayments?: boolean;
  patientName?: string;
  patientEmail?: string | null;
  patientPhone?: string | null;
}

/** What the plan's Quotes and Payments cards need from the patient page. */
interface PlanListsContext {
  showQuotes: boolean;
  showPayments: boolean;
  patientName?: string;
  patientEmail: string | null;
  patientPhone: string | null;
}

export function PlansTab({
  patientId,
  initialPlanId,
  showQuotes = false,
  showPayments = false,
  patientName,
  patientEmail = null,
  patientPhone = null,
}: PlansTabProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const userTz = useUserTimezone();

  const { data: plans, isLoading, isError, isFetching } = useQuery({
    queryKey: ['patient-plans', patientId],
    queryFn: () => getPlansByPatient(patientId),
  });

  const rows = useMemo(() => plans ?? [], [plans]);

  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
  const initialPlanApplied = useRef(false);

  useEffect(() => {
    if (rows.length === 0) {
      setSelectedPlanId(null);
      return;
    }
    // Apply the requested initial plan (e.g. one just created) exactly once, as
    // soon as it shows up in the list.
    if (!initialPlanApplied.current && initialPlanId) {
      if (rows.some((p) => p.planId === initialPlanId)) {
        initialPlanApplied.current = true;
        setSelectedPlanId(initialPlanId);
        return;
      }
      // Requested plan isn't in the list yet. If a fetch is still in flight the
      // freshly-created plan may still be arriving — wait for it rather than
      // falling back to a default and permanently consuming the one-shot flag
      // on a stale list.
      if (isFetching) return;
    }
    initialPlanApplied.current = true;
    if (selectedPlanId === null || !rows.some((p) => p.planId === selectedPlanId)) {
      setSelectedPlanId(rows[0].planId);
    }
  }, [rows, selectedPlanId, initialPlanId, isFetching]);

  const selectedPlan = useMemo(
    () => rows.find((p) => p.planId === selectedPlanId) ?? null,
    [rows, selectedPlanId],
  );

  useEffect(() => {
    if (!selectedPlanId) return;
    const opts = { staleTime: 30_000 };
    void queryClient.prefetchQuery({ queryKey: ['plan-detail', selectedPlanId], queryFn: () => getPlan(selectedPlanId), ...opts });
    void queryClient.prefetchQuery({ queryKey: ['plan-progress-bar', selectedPlanId], queryFn: () => getPlanProgressBar(selectedPlanId), ...opts });
    void queryClient.prefetchQuery({ queryKey: ['plan-stage-subs', selectedPlanId], queryFn: () => getPlanStageSubs(selectedPlanId), ...opts });
  }, [selectedPlanId, queryClient]);

  const handlePlanIdChange = useCallback(
    (newPlanId: number) => {
      setSelectedPlanId(newPlanId);
      queryClient.invalidateQueries({ queryKey: ['patient-plans', patientId] });
    },
    [patientId, queryClient],
  );

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    );
  }

  if (isError) {
    return <p className="py-4 text-sm text-red-600">Failed to load plans.</p>;
  }

  return (
    <div className="-m-6 flex h-[calc(100%+48px)] flex-col">
      {/* Fixed header — always visible at top, never scrolls */}
      <div className="flex-none bg-white px-6 py-3 border-b border-slate-200 z-10">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <h2 className="text-lg font-semibold text-slate-900 shrink-0">Plans</h2>
            {selectedPlan && (
              <div className="flex items-center gap-2 min-w-0 text-sm text-slate-500">
                <span className="text-slate-300">/</span>
                {selectedPlan.planTypeName && (
                  <span className="truncate font-medium text-slate-700">{selectedPlan.planTypeName}</span>
                )}
                <span className="shrink-0 font-mono text-xs text-slate-400">
                  {selectedPlan.displayId ?? `#${selectedPlan.planId}`}
                </span>
              </div>
            )}
          </div>
          <Button
            size="sm"
            leftIcon={<Plus className="h-4 w-4" />}
            onClick={() => navigate(`/leads/new?patientId=${patientId}`)}
          >
            Add Plan
          </Button>
        </div>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 min-h-0 overflow-y-auto p-6 space-y-4">
        {rows.length === 0 ? (
          <p className="py-4 text-sm text-slate-400 italic">No plans found.</p>
        ) : (
          <>
            <PlanSelector
              plans={rows}
              selectedPlan={selectedPlan}
              onSelect={(planId) => {
                setSelectedPlanId(planId);
              }}
              userTz={userTz}
            />

            {selectedPlan && (
              <SelectedPlanDetail
                plan={selectedPlan}
                patientId={patientId}
                onPlanIdChange={handlePlanIdChange}
                userTz={userTz}
                lists={{ showQuotes, showPayments, patientName, patientEmail, patientPhone }}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Collapsible Plan Selector ────────────────────────────────────────────────

function PlanSelector({
  plans,
  selectedPlan,
  onSelect,
  userTz,
}: {
  plans: PlanListItem[];
  selectedPlan: PlanListItem | null;
  onSelect: (planId: number) => void;
  userTz: string;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Column headers name staff roles the account may have renamed. Same cached
  // query the rest of the tab uses, so this costs no extra request.
  const { roles } = useRoleTitles();
  const doctorLabel = roles.find((r) => r.roleType === 'Doctor')?.roleTitle || 'Doctor';
  const coordLabel = roles.find((r) => r.roleType === 'Coordinator')?.roleTitle || 'Coordinator';

  // Close on click outside.
  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex w-full items-center justify-between rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-left transition-colors hover:bg-slate-50"
      >
        <div className="flex items-center gap-3">
          <span className="flex h-7 min-w-[28px] items-center justify-center rounded-full bg-blue-600 px-2 text-xs font-bold text-white">
            {plans.length}
          </span>
          <span className="text-sm font-medium text-slate-700">
            {plans.length === 1 ? 'plan' : 'plans'} available
          </span>
        </div>
        <ChevronDown
          className={`h-4 w-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown table */}
      {open && (
        <div className="absolute left-0 right-0 z-20 mt-1 overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg" style={{ maxHeight: 320 }}>
          <table className="w-full text-left text-sm">
            <thead className="sticky top-0 z-10 bg-slate-50">
              <tr className="border-b border-slate-200">
                <th className="whitespace-nowrap px-3 py-1.5 text-xs font-medium text-slate-500">Plan Type</th>
                <th className="whitespace-nowrap px-3 py-1.5 text-xs font-medium text-slate-500">Stage</th>
                <th className="whitespace-nowrap px-3 py-1.5 text-xs font-medium text-slate-500">Facility</th>
                <th className="whitespace-nowrap px-3 py-1.5 text-xs font-medium text-slate-500">{doctorLabel}</th>
                <th className="whitespace-nowrap px-3 py-1.5 text-xs font-medium text-slate-500">{coordLabel}</th>
                <th className="whitespace-nowrap px-3 py-1.5 text-xs font-medium text-slate-500">Date Added</th>
                <th className="whitespace-nowrap px-3 py-1.5 text-xs font-medium text-slate-500">Last Contact</th>
              </tr>
            </thead>
            <tbody>
              {plans.map((plan) => {
                const isSelected = plan.planId === selectedPlan?.planId;
                const added = formatDateTimeTz(plan.date, userTz);
                const lastContact = formatDateTimeTz(plan.lastContact, userTz);
                return (
                  <tr
                    key={plan.planId}
                    onClick={() => {
                      onSelect(plan.planId);
                      setOpen(false);
                    }}
                    className={`cursor-pointer border-b border-slate-100 transition-colors ${
                      isSelected
                        ? 'bg-blue-50 hover:bg-blue-100'
                        : 'hover:bg-slate-50'
                    }`}
                  >
                    <td className="whitespace-nowrap px-3 py-2">
                      <div className="text-slate-700">{plan.planTypeName ?? '—'}</div>
                      <div className="font-mono text-[11px] text-slate-400">{plan.displayId ?? `#${plan.planId}`}</div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2">
                      {plan.stageName ? (
                        <Badge variant={getPlanStageBadgeVariant(plan.stageName)} size="sm">
                          {plan.stageName}
                        </Badge>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                      {plan.stageSubName && (
                        <div className="text-[11px] text-slate-500 mt-0.5">
                          {plan.stageSubName}
                        </div>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-xs text-slate-700">
                      {plan.facilityName ?? '—'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-xs text-slate-700">
                      {plan.doctorName ?? '—'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-xs text-slate-700">
                      {plan.coordName ?? '—'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2">
                      <div className="text-xs text-slate-700">{added.date}</div>
                      {added.time && <div className="text-[11px] text-slate-400">{added.time}</div>}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2">
                      <div className="text-xs text-slate-700">{lastContact.date}</div>
                      {lastContact.time && <div className="text-[11px] text-slate-400">{lastContact.time}</div>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Selected Plan Detail ─────────────────────────────────────────────────────

function SelectedPlanDetail({
  plan,
  patientId,
  onPlanIdChange,
  userTz,
  lists,
}: {
  plan: PlanListItem;
  patientId: number;
  onPlanIdChange: (newPlanId: number) => void;
  userTz: string;
  lists: PlanListsContext;
}) {
  const queryClient = useQueryClient();

  // ── Plan Type lookup ──
  const { data: planTypes = [] } = useQuery({
    queryKey: ['admin', 'plan-types'],
    queryFn: listPlanTypes,
    staleTime: 5 * 60_000,
  });

  const planTypeMutation = useMutation({
    mutationFn: (planTypeId: number) => updatePlan(plan.planId, { planTypeId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient-plans', patientId] });
      queryClient.invalidateQueries({ queryKey: ['plan-progress-bar', plan.planId] });
      queryClient.invalidateQueries({ queryKey: ['plan-associated-entities', plan.planId] });
    },
    onError: () => toast.error('Failed to update plan type'),
  });

  const planTypeOptions = [
    { value: 0, label: 'None' },
    ...planTypes
      .filter((pt) => pt.active)
      .map((pt) => ({ value: pt.planTypeId, label: pt.name })),
  ];

  // ── Associated entities (appointments, memberships, packages) ──
  const { data: associatedEntities, isError: assocError } = useQuery({
    queryKey: ['plan-associated-entities', plan.planId],
    queryFn: () => getPlanAssociatedEntities(plan.planId),
    enabled: plan.planId > 0,
    staleTime: 60_000,
    retry: 1,
  });
  useEffect(() => {
    if (assocError) toast.error('Failed to load associated entities');
  }, [assocError]);

  const [checkingPlanType, setCheckingPlanType] = useState(false);
  const [planTypeAlert, setPlanTypeAlert] = useState<string | null>(null);
  const [pendingPlanTypeId, setPendingPlanTypeId] = useState<number | null>(null);

  const handlePlanTypeChange = useCallback(
    async (newPlanTypeId: number) => {
      const currentPlanTypeId = plan.planTypeId ?? 0;
      if (newPlanTypeId === currentPlanTypeId || newPlanTypeId === 0) {
        planTypeMutation.mutate(newPlanTypeId);
        return;
      }

      setCheckingPlanType(true);
      try {
        const result = await checkPlanType(plan.planId, newPlanTypeId);
        if (!result.allowed) {
          setPlanTypeAlert(result.message ?? 'Plan type change is not allowed.');
          return;
        }
        setPendingPlanTypeId(newPlanTypeId);
      } catch {
        setPlanTypeAlert('Failed to validate plan type change.');
      } finally {
        setCheckingPlanType(false);
      }
    },
    [plan.planId, plan.planTypeId, planTypeMutation],
  );

  const added = formatDateTimeTz(plan.date, userTz);
  const lastContact = formatDateTimeTz(plan.lastContact, userTz);

  return (
    <div className="space-y-4">
      {/* Plan summary card with pipeline + status */}
      <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 space-y-3">
        {/*
          Header row: Plan Type, Plan ID, dates.

          SCRUM-1385. This is one un-wrapped `justify-between` row holding a fixed 192px
          select and a right-aligned date block. At 375px the row is wider than the card,
          so the date block was squeezed to a few characters and broke one word per line
          ("Aug / 12, / 2026 / 9:04 / AM / EDT"). It was never in a separate column — it
          only looked like one. Below `md` the row stacks, the select takes the full
          width, and the dates left-align on their own line at full card width.
        */}
        <div className="flex items-center justify-between gap-4 max-md:flex-col max-md:items-stretch max-md:gap-2">
          <div className="flex items-center gap-2 max-md:flex-wrap">
            <span className="text-sm font-medium text-slate-600 whitespace-nowrap">Plan Type</span>
            <div className="w-48 max-md:w-full">
              <Select<number>
                options={planTypeOptions}
                value={plan.planTypeId ?? 0}
                onChange={(val) => {
                  if (val === null || val === undefined) return;
                  handlePlanTypeChange(val);
                }}
                disabled={planTypeMutation.isPending || checkingPlanType}
                loading={planTypeMutation.isPending || checkingPlanType}
              />
            </div>
            <span className="font-mono text-xs text-slate-400">
              {plan.displayId ?? `#${plan.planId}`}
            </span>
          </div>

          <div className="space-y-0.5 text-right max-md:text-left">
            {/* whitespace-nowrap is what actually stops the word-per-line break. */}
            <div className="max-md:whitespace-nowrap">
              <span className="text-[11px] font-medium text-slate-400">Added </span>
              <span className="text-xs text-slate-700">{added.date}</span>
              {added.time && <span className="text-[11px] text-slate-400"> {added.time}</span>}
            </div>
            <div className="max-md:whitespace-nowrap">
              <span className="text-[11px] font-medium text-slate-400">Last Contact </span>
              <span className="text-xs text-slate-700">{lastContact.date}</span>
              {lastContact.time && <span className="text-[11px] text-slate-400"> {lastContact.time}</span>}
            </div>
          </div>
        </div>

        {/* Pipeline */}
        <PlanProgressBar
          planId={plan.planId}
          patientId={patientId}
          onPlanIdChange={onPlanIdChange}
          stretch
        />

        {/* Status */}
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-slate-600 whitespace-nowrap">Status</span>
          <div className="w-48 max-md:w-full">
            <PlanStatusSelect planId={plan.planId} patientId={patientId} hideLabel />
          </div>
        </div>
      </div>

      {/* Plan details card */}
      <PlanDetailsSection planId={plan.planId} patientId={patientId} />

      {/* Plan-associated entities — hidden until data arrives, no flash */}
      <PlanAppointmentsSection
        appointments={associatedEntities?.appointments ?? []}
        planId={plan.planId}
        patientId={patientId}
        tz={userTz}
      />
      {/* Legacy's plan page lists Appointments, Quotes, then Payments here. */}
      <PlanQuotesSection
        planId={plan.planId}
        patientId={patientId}
        patientName={lists.patientName ?? plan.patientName}
        enabled={lists.showQuotes}
      />
      <PlanPaymentsSection
        planId={plan.planId}
        patientId={patientId}
        patientName={lists.patientName ?? plan.patientName}
        patientEmail={lists.patientEmail}
        patientPhone={lists.patientPhone}
        tz={userTz}
        enabled={lists.showPayments}
      />
      <PlanMembershipsSection
        memberships={associatedEntities?.memberships ?? []}
        planId={plan.planId}
        tz={userTz}
      />
      <PlanPackagesSection
        packages={associatedEntities?.packages ?? []}
        patientId={patientId}
        planId={plan.planId}
        tz={userTz}
      />
      <PlanProductsSection
        productTypeIds={associatedEntities?.productTypeIds ?? []}
        patientId={patientId}
        tz={userTz}
      />

      {/* Plan-scoped tasks */}
      <PlanTasksSection
        planId={plan.planId}
        patientId={patientId}
        planLabel={plan.displayId ? `#${plan.displayId}` : `Plan #${plan.planId}`}
        patientName={plan.patientName}
      />

      {/* Plan activity log */}
      <PlanActivitySection planId={plan.planId} tz={userTz} />

      {/* Alert modal — plan type check failed */}
      {planTypeAlert !== null && (
        <Modal
          isOpen
          onClose={() => setPlanTypeAlert(null)}
          title="Plan Type Change Not Allowed"
          size="sm"
          footer={
            <div className="flex justify-end">
              <Button onClick={() => setPlanTypeAlert(null)}>OK</Button>
            </div>
          }
        >
          <div className="flex gap-3 items-start">
            <div className="flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-full bg-red-100">
              <svg className="h-5 w-5 text-red-600" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.168 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <p className="text-sm text-slate-700">{planTypeAlert}</p>
              <div className="mt-3 rounded-md border border-blue-200 bg-blue-50 px-3 py-2">
                <p className="text-xs font-medium text-blue-800">Looking to switch to a different plan?</p>
                <p className="mt-0.5 text-xs text-blue-700">
                  Changing the plan type does not switch which plan you are viewing. To switch between plans, use the plan selector at the top of the page.
                </p>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Confirmation modal — warn about changing plan type on an active plan */}
      {pendingPlanTypeId !== null && (
        <Modal
          isOpen
          onClose={() => setPendingPlanTypeId(null)}
          title="Change Plan Type?"
          size="sm"
          footer={
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setPendingPlanTypeId(null)}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  planTypeMutation.mutate(pendingPlanTypeId);
                  setPendingPlanTypeId(null);
                }}
              >
                Yes, Change Plan Type
              </Button>
            </div>
          }
        >
          <p className="text-sm text-slate-700">
            You are about to change the plan type on an active plan. This will update the plan&rsquo;s pipeline and may affect associated stages and tasks.
          </p>
          <div className="mt-3 rounded-md border border-blue-200 bg-blue-50 px-3 py-2">
            <p className="text-xs font-medium text-blue-800">Looking to switch to a different plan?</p>
            <p className="mt-0.5 text-xs text-blue-700">
              Changing the plan type does not switch which plan you are viewing. To switch between plans, use the plan selector at the top of the page.
            </p>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── Plan Details Card + Edit Modal ───────────────────────────────────────────

function PlanDetailsSection({
  planId,
  patientId,
}: {
  planId: number;
  patientId: number;
}) {
  const queryClient = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);

  const { data: planDetail, isLoading: planLoading } = useQuery({
    queryKey: ['plan-detail', planId],
    queryFn: () => getPlan(planId),
    enabled: planId > 0,
    staleTime: 30_000,
  });

  const { roles } = useRoleTitles();
  const doctorLabel = roles.find((r) => r.roleType === 'Doctor')?.roleTitle || 'MD';
  const coordLabel = roles.find((r) => r.roleType === 'Coordinator')?.roleTitle || 'Coordinator';
  const nurseLabel = roles.find((r) => r.roleType === 'Nurse')?.roleTitle || 'Nurse';

  if (planLoading) {
    return (
      <div className="flex justify-center py-4">
        <Spinner />
      </div>
    );
  }

  if (!planDetail) return null;

  const referralParts: string[] = [];
  if (planDetail.referralCategoryName) referralParts.push(planDetail.referralCategoryName);
  if (planDetail.referralSourceName) referralParts.push(planDetail.referralSourceName);
  if (planDetail.referralFirstname || planDetail.referralLastname) {
    referralParts.push(
      [planDetail.referralFirstname, planDetail.referralLastname].filter(Boolean).join(' '),
    );
  }
  if (planDetail.referralOther) referralParts.push(planDetail.referralOther);
  const referralDisplay = referralParts.length > 0 ? referralParts.join(' → ') : '—';

  const leadTypeDisplay = LEAD_TYPE_OPTIONS.find((o) => o.value === planDetail.leadSourceId)?.label ?? '—';

  return (
    <>
      <div className="rounded-lg border border-slate-200 bg-white px-4 py-3">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-slate-700">Plan Details</h3>
          <button
            type="button"
            onClick={() => setEditOpen(true)}
            className="flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50 transition-colors"
          >
            <Pencil className="h-3 w-3" />
            Edit
          </button>
        </div>

        <div className="grid grid-cols-3 gap-x-6 gap-y-2">
          {/* Row 1: Facility, Lead Type, Lead Source */}
          <DetailCell label="Facility" value={planDetail.facilityName} />
          <DetailCell label="Lead Type" value={leadTypeDisplay} />
          <DetailCell label="Lead Source" value={referralDisplay} />

          {/* Row 2: Revenue Opportunity, Commitment Level, Timeframe */}
          <DetailCell
            label="Revenue Opportunity"
            value={planDetail.revenueOpportunity != null ? `$${planDetail.revenueOpportunity.toLocaleString()}` : null}
          />
          <DetailCell label="Commitment Level" value={getCommitmentLevelLabel(planDetail.ballpark)} />
          <DetailCell label="Timeframe" value={planDetail.timeframeName} />

          {/* Row 3: Doctor, Coordinator, Nurse */}
          <DetailCell label={doctorLabel} value={planDetail.doctorName} />
          <DetailCell label={coordLabel} value={planDetail.coordName} />
          <DetailCell label={nurseLabel} value={planDetail.nurseName} />

          {/* Row 4: Plan Note (full width) */}
          <div className="col-span-3 min-w-0 mt-1">
            <span className="text-[11px] font-medium text-slate-400">Plan Note</span>
            <p className="whitespace-pre-wrap text-xs text-slate-700 mt-0.5">{planDetail.inquiryNote || '—'}</p>
          </div>
        </div>
      </div>

      {editOpen && (
        <PlanDetailsEditModal
          planId={planId}
          patientId={patientId}
          planDetail={planDetail}
          roleTitles={{ doctor: doctorLabel, coordinator: coordLabel, nurse: nurseLabel }}
          onClose={() => setEditOpen(false)}
          onSaved={() => {
            setEditOpen(false);
            queryClient.invalidateQueries({ queryKey: ['patient-plans', patientId] });
            queryClient.invalidateQueries({ queryKey: ['plan-detail', planId] });
            queryClient.invalidateQueries({ queryKey: ['plan-progress-bar', planId] });
            queryClient.invalidateQueries({ queryKey: ['plan-stage-subs', planId] });
          }}
        />
      )}
    </>
  );
}

// ─── Plan Details Edit Modal ──────────────────────────────────────────────────

/** Exported for PlansTab.inactiveAssignee.test.tsx; mounted only by PlanDetailsSection. */
export function PlanDetailsEditModal({
  planId,
  // patientId is passed but not read here.
  planDetail,
  roleTitles,
  onClose,
  onSaved,
}: {
  planId: number;
  patientId: number;
  planDetail: Plan;
  roleTitles: { doctor: string; coordinator: string; nurse: string };
  onClose: () => void;
  onSaved: () => void;
}) {
  // ── Lookup data ──
  // Whoever this plan is already assigned to stays in their own list even after
  // they are de-activated, so the field shows their name rather than an empty
  // placeholder over a value it is still holding.
  const doctorInclude = toIncludeIds(planDetail.doctorId);
  const coordInclude = toIncludeIds(planDetail.coordId);
  const nurseInclude = toIncludeIds(planDetail.nurseId);

  const { data: doctors = [] } = useQuery({
    queryKey: staffQueryKey('doctors', doctorInclude),
    queryFn: () => listDoctors({ active: true, includeIds: doctorInclude }),
    staleTime: 5 * 60_000,
  });

  const { data: coordinators = [] } = useQuery({
    queryKey: staffQueryKey('coordinators', coordInclude),
    queryFn: () => listCoordinators({ active: true, includeIds: coordInclude }),
    staleTime: 5 * 60_000,
  });

  const { data: nurses = [] } = useQuery({
    queryKey: staffQueryKey('nurses', nurseInclude),
    queryFn: () => listNurses({ active: true, includeIds: nurseInclude }),
    staleTime: 5 * 60_000,
  });

  const { data: facilities = [] } = useQuery({
    queryKey: ['admin', 'facilities'],
    queryFn: listFacilities,
    staleTime: 5 * 60_000,
  });

  const { data: leadSourceCategories = [] } = useQuery<LeadSourceCategory[]>({
    queryKey: ['admin', 'lead-source-categories'],
    queryFn: listLeadSourceCategories,
    staleTime: 5 * 60_000,
  });

  const { data: timeframes = [] } = useQuery<Timeframe[]>({
    queryKey: ['admin', 'timeframes'],
    queryFn: listTimeframes,
    staleTime: 5 * 60_000,
  });

  // ── Form state ──
  const [facilityId, setFacilityId] = useState<number | null>(planDetail.facilityId);
  const [doctorId, setDoctorId] = useState<number | null>(planDetail.doctorId);
  const [coordId, setCoordId] = useState<number | null>(planDetail.coordId);
  const [nurseId, setNurseId] = useState<number | null>(planDetail.nurseId);
  const [timeframe, setTimeframe] = useState<string | null>(planDetail.timeframe);
  const [ballpark, setBallpark] = useState<number | null>(planDetail.ballpark);
  const [revenueOpportunity, setRevenueOpportunity] = useState<string>(
    planDetail.revenueOpportunity != null ? String(planDetail.revenueOpportunity) : '',
  );
  const [leadType, setLeadType] = useState<number | null>(planDetail.leadSourceId);
  const [referralCategoryId, setReferralCategoryId] = useState<number | null>(planDetail.referralGeneral);
  const [referralSourceId, setReferralSourceId] = useState<number | null>(planDetail.referralSpecific);
  // Prefer the patient the API resolved from `refSourceID` — it carries the real
  // patientId and DOB, so re-saving keeps the link and the nameplate matches what the
  // search dropdown showed when it was picked. Rows saved before the referring patient
  // was stored as an id have only the two name strings, so fall back to those; that
  // nameplate has no id and reads "DOB unknown", which is the pre-fix behaviour.
  const [referralPatient, setReferralPatient] = useState<import('@/types/patient.types').PatientSummary | null>(
    planDetail.referralPatient
      ? {
          patientId: planDetail.referralPatient.patientId,
          accountId: 0,
          firstName: planDetail.referralPatient.firstName,
          lastName: planDetail.referralPatient.lastName,
          displayName: [planDetail.referralPatient.firstName, planDetail.referralPatient.lastName]
            .filter(Boolean)
            .join(' '),
          displayId: planDetail.referralPatient.displayId != null
            ? String(planDetail.referralPatient.displayId)
            : null,
          dateOfBirth: planDetail.referralPatient.dateOfBirth,
          phone: null,
          homePhone: null,
          cellPhone: null,
          email: null,
          photoUrl: null,
          status: 'active',
          activePlanId: null,
          activePlanStage: null,
        }
      : planDetail.referralFirstname || planDetail.referralLastname
      ? {
          patientId: 0,
          accountId: 0,
          firstName: planDetail.referralFirstname ?? '',
          lastName: planDetail.referralLastname ?? '',
          displayName: [planDetail.referralFirstname, planDetail.referralLastname].filter(Boolean).join(' '),
          displayId: null,
          dateOfBirth: null,
          phone: null,
          homePhone: null,
          cellPhone: null,
          email: null,
          photoUrl: null,
          status: 'active',
          activePlanId: null,
          activePlanStage: null,
        }
      : null,
  );
  const [planNote, setPlanNote] = useState(planDetail.inquiryNote ?? '');

  // ── Referral source options (filtered by selected category) ──
  const referralCategoryOptions = useMemo(
    () => [
      { value: 0, label: 'Select One...' },
      ...leadSourceCategories
        .filter((c) => c.active === true || c.active === '1' || c.referralLevel1ID === referralCategoryId)
        .map((c) => ({ value: c.referralLevel1ID, label: c.name })),
    ],
    [leadSourceCategories, referralCategoryId],
  );

  const referralSourceOptions = useMemo(() => {
    const cat = leadSourceCategories.find((c) => c.referralLevel1ID === referralCategoryId);
    if (!cat) return [{ value: 0, label: 'Select One...' }];
    return [
      { value: 0, label: 'Select One...' },
      ...cat.leadSources
        .filter((s) => s.active === true || s.active === '1' || s.referralLevel2ID === referralSourceId)
        .map((s) => ({ value: s.referralLevel2ID, label: s.name })),
    ];
  }, [leadSourceCategories, referralCategoryId, referralSourceId]);

  // Which category means "referred by an existing patient" is decided by the API, which
  // matches on the category name the way legacy does. It used to be guessed here as the
  // lowest active category id, which picks the wrong category on any account whose
  // categories were not created in that order.
  const establishedPatientReferrerId = useMemo(() => {
    const match = leadSourceCategories.find((c) => c.isPatientReferralCategory);
    return match ? match.referralLevel1ID : null;
  }, [leadSourceCategories]);

  // False while the categories are still loading, which is also how it reads when the
  // category genuinely is not the patient one — so saving before they arrive would send
  // empty referral names and wipe a stored referring patient. Gate the save on this.
  const categoriesReady = leadSourceCategories.length > 0;
  const isEstablishedPatientSource = referralCategoryId !== null && referralCategoryId === establishedPatientReferrerId;

  // ── Save ──
  const saveMutation = useMutation({
    mutationFn: (payload: UpdatePlanPayload) => updatePlan(planId, payload),
    onSuccess: () => {
      toast.success('Plan details saved');
      onSaved();
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.error?.message || err?.userMessage || 'Failed to save plan details';
      toast.error(msg);
    },
  });

  function handleSave() {
    // The referral names below are derived from `isEstablishedPatientSource`, which
    // cannot be evaluated until the categories arrive — saving first would send two
    // empty names and erase a stored referring patient. The Save button is disabled
    // until then; this is the matching guard for any other caller.
    if (!categoriesReady) return;

    const revOpp = revenueOpportunity.trim();
    // These selects use 0 as the "None" option. Send 0 (not undefined) when
    // cleared so the backend actually unsets the field — it only updates keys
    // present in the request, so omitting them leaves the old value in place.
    saveMutation.mutate({
      facilityId: facilityId ?? 0,
      doctorId: doctorId ?? 0,
      coordId: coordId ?? 0,
      nurseId: nurseId ?? 0,
      timeframe: timeframe ?? undefined,
      ballpark: ballpark ?? undefined,
      revenueOpportunity: revOpp !== '' ? parseFloat(revOpp) : undefined,
      // Three columns, three values. `refSourceId` is the referring PATIENT's id —
      // sending the lead source id here is what overwrote the link and left only the
      // names behind, so the modal reopened with "DOB unknown".
      refSourceId: isEstablishedPatientSource ? (referralPatient?.patientId ?? 0) : 0,
      referralSpecific: (isEstablishedPatientSource && !referralPatient) ? 0 : (referralSourceId ?? 0),
      referralGeneral: (isEstablishedPatientSource && !referralPatient) ? 0 : (referralCategoryId ?? 0),
      leadSourceId: leadType ?? 0,
      referralFirstname: isEstablishedPatientSource && referralPatient ? referralPatient.firstName : '',
      referralLastname: isEstablishedPatientSource && referralPatient ? referralPatient.lastName : '',
      inquiryNote: planNote || '',
    });
  }

  // ── Options ──
  const facilityOptions = [
    { value: 0, label: 'None' },
    ...facilities
      .filter((f: Facility) => f.active && f.facilityId > 0)
      .map((f: Facility) => ({ value: f.facilityId, label: f.facilityName })),
  ];
  const doctorOptions = [
    { value: 0, label: 'None' },
    ...buildStaffOptions(doctors, (d) => d.doctorID, formatDoctorName),
  ];
  const coordOptions = [
    { value: 0, label: 'None' },
    ...buildStaffOptions(coordinators, (c) => c.coordID, formatCoordinatorName),
  ];
  const nurseOptions = [
    { value: 0, label: 'None' },
    ...buildStaffOptions(nurses, (n) => n.nurseID, formatNurseName),
  ];
  const leadTypeOptions = [
    { value: 0, label: 'None' },
    ...LEAD_TYPE_OPTIONS,
  ];
  const timeframeOptions = [
    { value: 0, label: 'None' },
    ...timeframes.map((tf) => ({ value: tf.timeframeId, label: tf.name })),
  ];
  const commitmentOptions = [
    { value: 0, label: 'None' },
    ...COMMITMENT_LEVEL_OPTIONS,
  ];

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Edit Plan Details"
      size="lg"
      footer={
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saveMutation.isPending || !categoriesReady}>
            {saveMutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      }
    >
      {/* Row 1: Facility, Revenue Opportunity */}
      <div className="grid gap-x-6 gap-y-4 md:grid-cols-2">
        <Select<number>
          label="Facility"
          options={facilityOptions}
          value={facilityId ?? 0}
          onChange={(val) => setFacilityId(val === 0 ? null : val)}
        />
        <Input
          label="Revenue Opportunity"
          type="number"
          placeholder="0.00"
          value={revenueOpportunity}
          onChange={(e) => setRevenueOpportunity(e.target.value)}
        />
      </div>

      {/* Row 2: Commitment Level, Timeframe */}
      <div className="mt-4 grid gap-x-6 gap-y-4 md:grid-cols-2">
        <Select<number>
          label="Commitment Level"
          options={commitmentOptions}
          value={ballpark ?? 0}
          onChange={(val) => setBallpark(val === 0 ? null : val)}
        />
        <Select<number>
          label="Timeframe"
          options={timeframeOptions}
          value={timeframe != null ? Number(timeframe) : 0}
          onChange={(val) => setTimeframe(val === 0 ? null : String(val))}
        />
      </div>

      {/* Row 3: Doctor, Coordinator, Nurse */}
      <div className="mt-4 grid gap-x-6 gap-y-4 md:grid-cols-3">
        <Select<number>
          label={roleTitles.doctor}
          options={doctorOptions}
          value={doctorId ?? 0}
          onChange={(val) => setDoctorId(val === 0 ? null : val)}
          searchable
        />
        <Select<number>
          label={roleTitles.coordinator}
          options={coordOptions}
          value={coordId ?? 0}
          onChange={(val) => setCoordId(val === 0 ? null : val)}
          searchable
        />
        <Select<number>
          label={roleTitles.nurse}
          options={nurseOptions}
          value={nurseId ?? 0}
          onChange={(val) => setNurseId(val === 0 ? null : val)}
          searchable
        />
      </div>

      {/* Row 4: Lead Type */}
      <div className="mt-4 grid gap-x-6 gap-y-4 md:grid-cols-2">
        <Select<number>
          label="Lead Type"
          options={leadTypeOptions}
          value={leadType ?? 0}
          onChange={(val) => setLeadType(val === 0 ? null : val)}
        />
      </div>

      {/* Row 5: Lead Source */}
      <div className="mt-4 grid gap-x-6 gap-y-4 md:grid-cols-2">
        <Select<number>
          label="Lead Source Category"
          options={referralCategoryOptions}
          value={referralCategoryId ?? 0}
          onChange={(val) => {
            setReferralCategoryId(val === 0 ? null : val);
            setReferralSourceId(null);
          }}
        />
        {referralCategoryId != null && referralSourceOptions.length > 1 && (
          <Select<number>
            label="Lead Source"
            options={referralSourceOptions}
            value={referralSourceId ?? 0}
            onChange={(val) => setReferralSourceId(val === 0 ? null : val)}
          />
        )}
      </div>

      {isEstablishedPatientSource && (
        <div className="mt-4">
          <p className="mb-1 text-sm font-medium text-slate-700">Referring Patient</p>
          <PatientSearchInput
            selectedPatient={referralPatient}
            onSelect={setReferralPatient}
            placeholder="Search for referring patient…"
          />
        </div>
      )}

      <div className="mt-4">
        <Textarea
          label="Plan Note"
          value={planNote}
          onChange={(e) => setPlanNote(e.target.value)}
          rows={3}
        />
      </div>
    </Modal>
  );
}

// ─── Plan-Associated Appointments Section ─────────────────────────────────────

export function PlanAppointmentsSection({
  appointments,
  planId,
  patientId,
  tz,
}: {
  appointments: PlanAssociatedAppointment[];
  planId: number;
  patientId: number;
  tz: string;
}) {
  const queryClient = useQueryClient();
  const [collapsed, setCollapsed] = useState(true);
  const [selectedAppt, setSelectedAppt] = useState<Appointment | null>(null);
  const [loadingApptId, setLoadingApptId] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const count = appointments.length;

  const refreshAppointments = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['plan-associated-entities', planId] });
    invalidatePatientAppointments(queryClient, patientId);
  }, [queryClient, planId, patientId]);

  const handleRowClick = useCallback(async (appointmentId: number) => {
    setLoadingApptId(appointmentId);
    try {
      const full = await appointmentsService.getAppointment(appointmentId);
      setSelectedAppt(full);
    } catch {
      toast.error('Failed to load appointment details');
    } finally {
      setLoadingApptId(null);
    }
  }, []);

  if (count === 0) return null;

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <button
        type="button"
        className="flex w-full items-center gap-2 px-4 py-2.5 text-left hover:bg-slate-50 transition-colors rounded-t-xl"
        onClick={() => setCollapsed((c) => !c)}
      >
        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${collapsed ? '-rotate-90' : ''}`} />
        <Calendar className="h-4 w-4 text-blue-500" />
        <h3 className="text-sm font-semibold text-slate-700">Appointments</h3>
        <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-blue-100 px-1.5 text-[10px] font-bold text-blue-600 leading-none">
          {count}
        </span>
      </button>

      {!collapsed && (
        <div className="space-y-2 px-4 pb-4">
          {/* In the body, not the header: the header is one toggle button. */}
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              className={ACTION_BUTTON_CLASS}
              onClick={() => setCreateOpen(true)}
            >
              <Plus className="h-3 w-3" />
              Add Appointment
            </button>
          </div>
          <div className="overflow-auto rounded-md border border-slate-100">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50 text-left text-[11px] font-medium text-slate-500">
                  <th className="px-3 py-2">Date / Time</th>
                  <th className="px-3 py-2">Type</th>
                  <th className="px-3 py-2">Resources</th>
                  <th className="px-3 py-2">Duration</th>
                  <th className="px-3 py-2">Facility</th>
                  <th className="px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {appointments.map((appt) => {
                  const dt = formatDateTimeTz(appt.startTime, tz);
                  return (
                    <tr
                      key={appt.appointmentId}
                      className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50 cursor-pointer"
                      onClick={() => handleRowClick(appt.appointmentId)}
                    >
                      <td className="px-3 py-2 whitespace-nowrap">
                        {loadingApptId === appt.appointmentId ? (
                          <Spinner className="h-3 w-3" />
                        ) : (
                          <>
                            <span className="text-slate-700">{dt.date}</span>
                            {dt.time && <span className="ml-1 text-slate-400">{dt.time}</span>}
                          </>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <span className="inline-flex items-center gap-1.5">
                          {appt.appointmentTypeColor && (
                            <span
                              className="inline-block h-2.5 w-2.5 rounded-full shrink-0"
                              style={{ backgroundColor: appt.appointmentTypeColor }}
                            />
                          )}
                          <span className="text-slate-700">{appt.appointmentTypeName || '—'}</span>
                        </span>
                      </td>
                      <td className="px-3 py-2 text-slate-600">
                        {appt.resources?.length ? appt.resources.join(', ') : '—'}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-slate-600">
                        {appt.durationMinutes ? `${appt.durationMinutes} min` : '—'}
                      </td>
                      <td className="px-3 py-2 text-slate-600">{appt.facilityName || '—'}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${
                          appt.status === 'Cancelled' ? 'bg-red-50 text-red-600'
                            : appt.status === 'Checked Out' ? 'bg-green-50 text-green-600'
                              : appt.status === 'Checked In' ? 'bg-amber-50 text-amber-600'
                                : appt.status === 'Confirmed' ? 'bg-blue-50 text-blue-600'
                                  : 'bg-slate-100 text-slate-600'
                        }`}>
                          {appt.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selectedAppt && (
        <AppointmentDetailModal
          isOpen
          onClose={() => setSelectedAppt(null)}
          appointment={selectedAppt}
          onUpdated={(updated) => {
            setSelectedAppt(updated);
            queryClient.invalidateQueries({ queryKey: ['plan-associated-entities', planId] });
          }}
        />
      )}

      {createOpen && (
        <AppointmentCreateModal
          isOpen
          onClose={() => setCreateOpen(false)}
          defaultPatientId={patientId}
          // Books it on this plan (scheduled_appointments.patientPlanID).
          defaultPatientPlanId={planId}
          onSuccess={(appointment) => {
            setCreateOpen(false);
            refreshAppointments();
            // Straight into the new appointment, as the Appointments tab does.
            setSelectedAppt(appointment);
          }}
        />
      )}
    </div>
  );
}

// ─── Plan-Associated Memberships Section ──────────────────────────────────────

function PlanMembershipsSection({
  memberships,
  planId,
  tz,
}: {
  memberships: PlanAssociatedMembership[];
  planId: number;
  tz: string;
}) {
  const queryClient = useQueryClient();
  const [collapsed, setCollapsed] = useState(true);
  const [selectedRecurringId, setSelectedRecurringId] = useState<number | null>(null);
  const count = memberships.length;
  const sorted = useMemo(
    () => [...memberships].sort((a, b) => (a.status === 'Active' ? 0 : 1) - (b.status === 'Active' ? 0 : 1)),
    [memberships],
  );

  if (count === 0) return null;

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <button
        type="button"
        className="flex w-full items-center gap-2 px-4 py-2.5 text-left hover:bg-slate-50 transition-colors rounded-t-xl"
        onClick={() => setCollapsed((c) => !c)}
      >
        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${collapsed ? '-rotate-90' : ''}`} />
        <CreditCard className="h-4 w-4 text-violet-500" />
        <h3 className="text-sm font-semibold text-slate-700">Memberships</h3>
        <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-violet-100 px-1.5 text-[10px] font-bold text-violet-600 leading-none">
          {count}
        </span>
      </button>

      {!collapsed && (
        <div className="px-4 pb-4">
          <div className="overflow-auto rounded-md border border-slate-100">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50 text-left text-[11px] font-medium text-slate-500">
                  <th className="px-3 py-2">Title</th>
                  <th className="px-3 py-2">Amount</th>
                  <th className="px-3 py-2">Start Date</th>
                  <th className="px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((mem) => {
                  const dt = formatDateTimeTz(mem.startDate, tz);
                  return (
                    <tr
                      key={mem.recurringId}
                      className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50 cursor-pointer"
                      onClick={() => setSelectedRecurringId(mem.recurringId)}
                    >
                      <td className="px-3 py-2 text-slate-700 font-medium">{mem.membershipTitle}</td>
                      <td className="px-3 py-2 text-slate-700 tabular-nums">
                        {`$${mem.curAmount.toFixed(2)}`}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-slate-600">{dt.date}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${
                          mem.status === 'Active' ? 'bg-green-50 text-green-600'
                            : mem.status === 'Frozen' ? 'bg-blue-50 text-blue-600'
                              : 'bg-slate-100 text-slate-500'
                        }`}>
                          {mem.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <RecurringDetailModal
        recurringId={selectedRecurringId}
        open={selectedRecurringId !== null}
        onClose={() => setSelectedRecurringId(null)}
        onChanged={() => {
          queryClient.invalidateQueries({ queryKey: ['plan-associated-entities', planId] });
        }}
      />
    </div>
  );
}

// ─── Plan-Associated Packages Section ─────────────────────────────────────────

function PlanPackagesSection({
  packages,
  patientId,
  planId,
  tz,
}: {
  packages: PlanAssociatedPackage[];
  patientId: number;
  planId: number;
  tz: string;
}) {
  const queryClient = useQueryClient();
  const [collapsed, setCollapsed] = useState(true);
  const [selectedPkgId, setSelectedPkgId] = useState<number | null>(null);
  const count = packages.length;

  if (count === 0) return null;

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <button
        type="button"
        className="flex w-full items-center gap-2 px-4 py-2.5 text-left hover:bg-slate-50 transition-colors rounded-t-xl"
        onClick={() => setCollapsed((c) => !c)}
      >
        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${collapsed ? '-rotate-90' : ''}`} />
        <Package className="h-4 w-4 text-amber-500" />
        <h3 className="text-sm font-semibold text-slate-700">Packages</h3>
        <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-amber-100 px-1.5 text-[10px] font-bold text-amber-600 leading-none">
          {count}
        </span>
      </button>

      {!collapsed && (
        <div className="px-4 pb-4">
          <div className="overflow-auto rounded-md border border-slate-100">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50 text-left text-[11px] font-medium text-slate-500">
                  <th className="px-3 py-2">Name</th>
                  <th className="px-3 py-2">Purchase Date</th>
                  <th className="px-3 py-2">Price</th>
                  <th className="px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {packages.map((pkg) => {
                  const dt = formatDateTimeTz(pkg.purchaseDate, tz);
                  return (
                    <tr
                      key={pkg.patientPackageId}
                      className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50 cursor-pointer"
                      onClick={() => setSelectedPkgId(pkg.patientPackageId)}
                    >
                      <td className="px-3 py-2 text-slate-700 font-medium">{pkg.packageName}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-slate-600">{dt.date}</td>
                      <td className="px-3 py-2 text-slate-700 tabular-nums">
                        {pkg.price > 0 ? `$${pkg.price.toFixed(2)}` : '—'}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${
                          pkg.active ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-500'
                        }`}>
                          {pkg.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <PackageDetailModal
        open={selectedPkgId !== null}
        onClose={() => {
          setSelectedPkgId(null);
          queryClient.invalidateQueries({ queryKey: ['plan-associated-entities', planId] });
        }}
        patientId={patientId}
        patientPackageId={selectedPkgId}
      />
    </div>
  );
}

// ─── Plan-Associated Products Section ──────────────────────────────────────────

function filterProductsByProductType(
  data: ProductTypesResponse,
  productTypeIds: Set<number>,
): { types: ProductGroup[]; others: ProductGroup[] } {
  const rawTypes = data?.data?.['product-types'] ?? {};
  const rawOthers = data?.data?.['product-others'] ?? {};

  const types = Object.values(rawTypes)
    .filter((g): g is ProductGroup =>
      g != null && g.productTypeID != null && productTypeIds.has(g.productTypeID),
    );
  const others = Object.values(rawOthers)
    .filter((g): g is ProductGroup =>
      g != null && g.productTypeID != null && productTypeIds.has(g.productTypeID),
    );

  return { types, others };
}

function PlanProductsSection({
  productTypeIds,
  patientId,
  // tz is passed but nothing in here renders a date, so it is simply
  // surplus. Kept on the props type so the caller still compiles.
}: {
  productTypeIds: number[];
  patientId: number;
  tz: string;
}) {
  const [collapsed, setCollapsed] = useState(true);
  const [selectedAppt, setSelectedAppt] = useState<Appointment | null>(null);

  const { data: productData, isLoading } = useQuery<ProductTypesResponse>({
    queryKey: ['patient-product-types', patientId],
    queryFn: () => getPatientProductTypes(String(patientId)),
    enabled: patientId > 0 && productTypeIds.length > 0,
    staleTime: 60_000,
  });

  const idSet = useMemo(() => new Set(productTypeIds), [productTypeIds]);

  const filtered = useMemo(() => {
    if (!productData) return { types: [], others: [] };
    return filterProductsByProductType(productData, idSet);
  }, [productData, idSet]);

  const count = filtered.types.length + filtered.others.length;

  const handleOpenAppointment = useCallback(async (appointmentId: number) => {
    try {
      const full = await appointmentsService.getAppointment(appointmentId);
      setSelectedAppt(full);
    } catch {
      toast.error('Failed to load appointment details');
    }
  }, []);

  if (productTypeIds.length === 0) return null;
  if (!isLoading && count === 0) return null;

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <button
        type="button"
        className="flex w-full items-center gap-2 px-4 py-2.5 text-left hover:bg-slate-50 transition-colors rounded-t-xl"
        onClick={() => setCollapsed((c) => !c)}
      >
        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${collapsed ? '-rotate-90' : ''}`} />
        <ShoppingBag className="h-4 w-4 text-orange-500" />
        <h3 className="text-sm font-semibold text-slate-700">Products</h3>
        {count > 0 && (
          <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-orange-100 px-1.5 text-[10px] font-bold text-orange-600 leading-none">
            {count}
          </span>
        )}
      </button>

      {!collapsed && (
        <div className="px-4 pb-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
              <span className="ml-2 text-xs text-slate-400">Loading products…</span>
            </div>
          ) : count === 0 ? (
            <p className="py-3 text-center text-xs italic text-slate-400">No products for this plan type</p>
          ) : (
            <div className="space-y-1">
              {filtered.types.map((g) => (
                <ProductGroupCard
                  key={`pt-${g.productTypeID}`}
                  group={g}
                  listType="product-types"
                  onAction={async () => {}}
                  onOpenAppointment={handleOpenAppointment}
                  readOnly
                />
              ))}
              {filtered.others.map((g) => (
                <ProductGroupCard
                  key={`po-${g.productID ?? g.productTypeID}`}
                  group={g}
                  listType="product-others"
                  onAction={async () => {}}
                  onOpenAppointment={handleOpenAppointment}
                  readOnly
                />
              ))}
            </div>
          )}
        </div>
      )}

      {selectedAppt && (
        <AppointmentDetailModal
          isOpen
          onClose={() => setSelectedAppt(null)}
          appointment={selectedAppt}
          onUpdated={(updated) => setSelectedAppt(updated)}
        />
      )}
    </div>
  );
}

// ─── Plan-Scoped Tasks Section ────────────────────────────────────────────────

// Exported for tests: mounting it directly avoids the whole plan page.
export function PlanTasksSection({
  planId,
  patientId,
  planLabel,
  patientName,
}: {
  planId: number;
  patientId: number;
  planLabel: string;
  patientName: string;
}) {
  const queryClient = useQueryClient();
  const [activeFilter, setActiveFilter] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);

  const { data: savedTab } = useQuery({
    queryKey: ['task-filter-tab'],
    queryFn: getTaskFilterTab,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (activeFilter === null && savedTab != null) {
      setActiveFilter(savedTab);
    }
  }, [savedTab, activeFilter]);

  const handleFilterChange = useCallback((flTasks: number) => {
    setActiveFilter(flTasks);
    saveTaskFilterTab(flTasks).catch(() => {});
    queryClient.setQueryData(['task-filter-tab'], flTasks);
  }, [queryClient]);

  const resolvedFilter = activeFilter ?? 1;

  const { data: tasksPage, isLoading, isError } = useQuery({
    queryKey: ['plan-tasks', planId, resolvedFilter],
    queryFn: () => listTasks({ planId, flTasks: resolvedFilter, pageSize: 200 }),
    enabled: planId > 0,
  });

  const { data: counts } = useQuery<TaskCounts>({
    queryKey: ['plan-task-counts', planId],
    queryFn: () => getTaskCounts({ planId }),
    enabled: planId > 0,
  });

  // The header badge counts only the team's past-due work: a task assigned to
  // Prospera is done by Prospera. The sub-tab counts above keep matching this
  // card's list, which still shows every task. Same key prefix, so the
  // refreshTasks() invalidation below refetches both.
  const { data: humanCounts } = useQuery<TaskCounts>({
    queryKey: ['plan-task-counts', planId, 'human'],
    queryFn: () => getTaskCounts({ planId, taskSource: 'human' }),
    enabled: planId > 0,
  });

  const { data: users = [] } = useQuery<AdminUser[]>({
    queryKey: ['admin-users'],
    queryFn: listUsers,
    staleTime: 5 * 60_000,
  });

  const completeMutation = useMutation({
    mutationFn: ({ taskId }: { taskId: number }) => updateTask(taskId, { status: 'complete' }),
    onSuccess: () => refreshTasks(),
  });

  const tasks = tasksPage?.data ?? [];

  function refreshTasks() {
    void queryClient.invalidateQueries({ queryKey: ['plan-tasks', planId] });
    void queryClient.invalidateQueries({ queryKey: ['plan-task-counts', planId] });
    // The sidebar's Tasks count and the Dashboard's Pending Tasks badge.
    void queryClient.invalidateQueries({ queryKey: MY_TASK_COUNTS_KEY });
  }

  const rowRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [useDropdown, setUseDropdown] = useState(false);
  const [collapsed, setCollapsed] = useState(true);

  useEffect(() => {
    const row = rowRef.current;
    const measure = measureRef.current;
    if (!row || !measure) return;
    const check = () => {
      const available = row.clientWidth;
      const needed = measure.scrollWidth;
      setUseDropdown(needed > available - 2);
    };
    check();
    const ro = new ResizeObserver(check);
    ro.observe(row);
    return () => ro.disconnect();
  }, [counts, collapsed]);

  const pastDueCount = humanCounts?.pastDue ?? 0;

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
      {/* Collapsible header */}
      <button
        type="button"
        className="flex w-full items-center gap-2 px-4 py-2.5 text-left hover:bg-slate-50 transition-colors rounded-t-xl"
        onClick={() => setCollapsed(c => !c)}
      >
        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${collapsed ? '-rotate-90' : ''}`} />
        <ClipboardList className="h-4 w-4 text-emerald-500" />
        <h3 className="text-sm font-semibold text-slate-700">Tasks</h3>
        {pastDueCount > 0 && (
          <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-emerald-100 px-1.5 text-[10px] font-bold text-emerald-600 leading-none" title={`${pastDueCount} past due`}>
            {pastDueCount}
          </span>
        )}
      </button>

      {!collapsed && (
        <div className="space-y-3 px-4 pb-4">
          {/* Hidden measure strip */}
          <div ref={measureRef} aria-hidden className="pointer-events-none invisible absolute flex items-center gap-1 whitespace-nowrap" style={{ height: 0, overflow: 'hidden' }}>
            {TASK_FILTER_TABS.map((tab) => (
              <span key={tab.flTasks} className="inline-flex shrink-0 items-center gap-1.5 px-3 py-1.5 text-xs font-medium">
                {tab.label}
                {tab.showCount && (counts?.[tab.countKey] ?? 0) > 0 ? (
                  <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-bold leading-none">
                    {counts?.[tab.countKey] ?? 0}
                  </span>
                ) : null}
              </span>
            ))}
          </div>

          {/* Filter tabs + Add Task inline */}
          <div ref={rowRef} className="flex items-center gap-2 border-b border-slate-200 pb-1">
            {useDropdown ? (
              <select
                value={resolvedFilter}
                onChange={(e) => handleFilterChange(Number(e.target.value))}
                className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-600 focus:outline-none focus:ring-1 focus:ring-primary"
              >
                {TASK_FILTER_TABS.map((tab) => {
                  const count = counts?.[tab.countKey] ?? 0;
                  return (
                    <option key={tab.flTasks} value={tab.flTasks}>
                      {tab.label}{tab.showCount && count > 0 ? ` (${count})` : ''}
                    </option>
                  );
                })}
              </select>
            ) : (
              // Same treatment as the Activity strip below: scroll rather than clip.
              <div className="flex items-center gap-1 min-w-0 max-md:overflow-x-auto max-md:scrollbar-hide">
                {TASK_FILTER_TABS.map((tab) => {
                  const isActive = resolvedFilter === tab.flTasks;
                  const count = counts?.[tab.countKey] ?? 0;
                  return (
                    <button
                      key={tab.flTasks}
                      type="button"
                      onClick={() => handleFilterChange(tab.flTasks)}
                      // The selected tab carries `border-b-2` and the others carry no
                      // bottom border at all, so the active one is 2px taller and its
                      // grey box rides above the row — the "sits higher / looks broken"
                      // in SCRUM-1389. A transparent border of the same width squares
                      // them up. Scoped `max-md:` so the desktop row keeps the geometry
                      // it has today; DetailSubNav uses this idiom unscoped.
                      className={`inline-flex shrink-0 items-center gap-1.5 rounded-t-md px-3 py-1.5 text-xs font-medium transition-colors ${
                        isActive
                          ? 'border-b-2 border-primary text-primary bg-primary/10'
                          : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50 max-md:border-b-2 max-md:border-transparent'
                      }`}
                    >
                      {tab.label}
                      {tab.showCount && count > 0 ? (
                        <span className={`inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-bold leading-none ${
                          isActive ? 'bg-primary/15 text-primary' : 'bg-amber-100 text-amber-600'
                        }`}>
                          {count}
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            )}

            <button
              type="button"
              className="ml-auto shrink-0 inline-flex items-center gap-1 rounded-md bg-primary/15 px-2.5 py-1 text-xs font-medium text-primary hover:bg-primary/25 transition-colors"
              onClick={() => setCreateOpen(true)}
            >
              <Plus className="h-3 w-3" />
              Add Task
            </button>
          </div>

          {/* Task table */}
          {isLoading ? (
            <div className="flex justify-center py-6">
              <Spinner />
            </div>
          ) : isError ? (
            <p className="py-4 text-sm text-red-600">Failed to load tasks.</p>
          ) : tasks.length === 0 ? (
            <p className="py-4 text-sm text-slate-400 italic">No tasks found.</p>
          ) : (
            <div className="rounded-lg border border-slate-200 overflow-auto" style={{ maxHeight: 260 }}>
              <table className="min-w-full divide-y divide-slate-100 text-sm">
                <thead className="sticky top-0 z-10 bg-slate-50">
                  <tr className="text-[11px] font-medium text-slate-400 tracking-normal">
                    <th className="px-4 py-2 text-left">Task</th>
                    <th className="px-4 py-2 text-left">Status</th>
                    <th className="px-4 py-2 text-left">Priority</th>
                    <th className="px-4 py-2 text-left">Occurs</th>
                    <th className="px-4 py-2 text-left">Added</th>
                    <th className="px-4 py-2 text-left">Due</th>
                    <th className="px-4 py-2 text-left">Done</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {tasks.map((task) => {
                    const status = deriveTaskStatus(task);
                    const isComplete = status.label === 'Completed';
                    const isInactive = status.label === 'Inactive';
                    const taskId = task.planTaskID ?? task.taskItemID ?? 0;
                    const dueStr = task.lastDue ?? task.dueDate ?? null;
                    const addedStr = task.dateStarted ?? null;
                    const doneStr = task.dateCompleted && task.dateCompleted !== '0000-00-00' && task.dateCompleted !== '0000-00-00 00:00:00'
                      ? task.dateCompleted : null;
                    const showDoneButton = !isComplete && !isInactive && !(status.label === 'Upcoming' && task.required === 1);

                    return (
                      <tr
                        key={taskId}
                        className="cursor-pointer hover:bg-slate-50"
                        onClick={() => setSelectedTask(task)}
                      >
                        <td className="px-4 py-2.5 text-slate-800 font-medium">
                          {task.name ?? task.title ?? '—'}
                        </td>
                        <td className={`px-4 py-2.5 text-sm ${status.className}`}>
                          {status.label}
                        </td>
                        <td className="px-4 py-2.5">
                          <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                            task.priority === 3 ? 'bg-orange-100 text-orange-700'
                              : task.priority === 1 ? 'bg-slate-100 text-slate-500'
                                : 'bg-blue-100 text-blue-700'
                          }`}>
                            {priorityLabel(task.priority)}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-[11px] text-slate-500">
                          {task.recurring ? 'Recurring' : 'Once'}
                        </td>
                        <td className="px-4 py-2.5 text-[11px] text-slate-500">
                          {addedStr ? formatShortDate(addedStr) : '—'}
                        </td>
                        <td className="px-4 py-2.5 text-[11px] text-slate-500">
                          {dueStr ? formatShortDate(dueStr) : 'N/A'}
                        </td>
                        <td className="px-4 py-2.5">
                          {isComplete && doneStr ? (
                            <span className="text-sm text-slate-500">{formatShortDate(doneStr)}</span>
                          ) : showDoneButton ? (
                            <TaskDoneButton
                              taskId={taskId}
                              size="xs"
                              disabled={completeMutation.isPending}
                              onComplete={() => completeMutation.mutate({ taskId })}
                              onRescheduled={() => refreshTasks()}
                            />
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {createOpen && (
        <TaskModal
          users={users}
          initialPlan={{ planId, planLabel, patientId, patientName }}
          onClose={() => setCreateOpen(false)}
          onSaved={refreshTasks}
        />
      )}
      {selectedTask && (
        <TaskModal
          item={selectedTask}
          users={users}
          onClose={() => setSelectedTask(null)}
          onSaved={refreshTasks}
        />
      )}
    </div>
  );
}

// ─── Plan Activity Section ────────────────────────────────────────────────────

const ACTIVITY_TABS = [
  { key: 'notes',   label: 'Notes' },
  { key: 'stages',  label: 'Stages' },
  { key: 'comms',   label: 'Comms' },
  { key: 'history', label: 'History' },
] as const;

type ActivityTabKey = (typeof ACTIVITY_TABS)[number]['key'];

const COMM_EXCLUDE_TYPES: CommLogType[] = ['note', 'stage_change', 'document_sent'];

function ActivityTypeIcon({ type }: { type: CommLogType }) {
  const cls = 'h-4 w-4 shrink-0';
  switch (type) {
    case 'email_sent':
    case 'email_received':
      return <Mail className={cls} />;
    case 'sms_sent':
    case 'sms_received':
      return <MessageSquare className={cls} />;
    case 'call_logged':
      return <Phone className={cls} />;
    case 'note':
      return <FileText className={cls} />;
    case 'stage_change':
      return <ArrowRight className={cls} />;
    case 'autoresponder':
      return <Bell className={cls} />;
    default:
      return <FileText className={cls} />;
  }
}

const ACTIVITY_TYPE_LABELS: Record<CommLogType, string> = {
  email_sent: 'Email Sent',
  email_received: 'Email Received',
  sms_sent: 'SMS Sent',
  sms_received: 'SMS Received',
  call_logged: 'Call',
  chat: 'Chat',
  note: 'Note',
  stage_change: 'Stage Change',
  document_sent: 'Document',
  autoresponder: 'Autoresponder',
};

const ACTIVITY_TYPE_COLOR: Record<CommLogType, string> = {
  email_sent: 'text-indigo-600 bg-indigo-50',
  email_received: 'text-indigo-400 bg-indigo-50',
  sms_sent: 'text-emerald-600 bg-emerald-50',
  sms_received: 'text-emerald-400 bg-emerald-50',
  call_logged: 'text-amber-600 bg-amber-50',
  chat: 'text-slate-500 bg-slate-100',
  note: 'text-slate-600 bg-slate-100',
  stage_change: 'text-orange-600 bg-orange-50',
  document_sent: 'text-blue-600 bg-blue-50',
  autoresponder: 'text-purple-600 bg-purple-50',
};

function pacificDatetimeToUtcMs(dateStr: string): number | null {
  const parsed = parsePacificDatetime(dateStr);
  if (!parsed) return null;
  const utcWall = pacificToWallClock(parsed.date, parsed.time, 'UTC');
  const [y, mo, d] = utcWall.date.split('-').map(Number);
  const [h, mi] = utcWall.time.split(':').map(Number);
  return Date.UTC(y, mo - 1, d, h, mi);
}

function activityRelativeTime(dateStr: string, tz: string): string {
  const utcMs = pacificDatetimeToUtcMs(dateStr);
  if (utcMs === null) return '—';
  const diff = Date.now() - utcMs;
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  const { date } = formatDateTimeTz(dateStr, tz);
  return date;
}

function formatActivityFullDate(dateStr: string | null | undefined, tz: string): string {
  if (!dateStr) return '';
  const { date, time } = formatDateTimeTz(dateStr, tz);
  return time ? `${date}, ${time}` : date;
}

function CommEntryCard({ entry, tz }: { entry: CommLogEntry; tz: string }) {
  const [expanded, setExpanded] = useState(false);
  const colorCls = ACTIVITY_TYPE_COLOR[entry.type] ?? 'text-slate-500 bg-slate-100';
  const authorName = entry.userId
    ? `${entry.authorFirstName ?? entry.userFirstName ?? ''} ${entry.authorLastName ?? entry.userLastName ?? ''}`.trim()
    : null;
  const isLong = (entry.message?.length ?? 0) > 160;

  const displayTitle = entry.type === 'stage_change' && entry.stageName
    ? `Stage changed to ${entry.stageName}`
    : entry.subject || ACTIVITY_TYPE_LABELS[entry.type];

  const titleRendered = linkifyHtml(displayTitle ?? '');
  const titleHasHtml = /<[a-z][\s\S]*?>/i.test(titleRendered);
  const messageRendered = entry.message ? linkifyHtml(entry.message) : '';

  const fullDate = formatActivityFullDate(entry.createdAt, tz);

  return (
    <div className="flex gap-3 p-3 bg-white border border-slate-200 rounded-xl hover:border-slate-300 transition-colors">
      <div className={`flex-shrink-0 mt-0.5 p-1.5 rounded-lg ${colorCls}`}>
        <ActivityTypeIcon type={entry.type} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          {titleHasHtml ? (
            <span
              className="font-semibold text-sm text-slate-900 truncate prose prose-sm max-w-none [&>p]:m-0"
              dangerouslySetInnerHTML={{ __html: titleRendered }}
            />
          ) : (
            <span className="font-semibold text-sm text-slate-900 truncate">
              {displayTitle}
            </span>
          )}
          <span className="text-[11px] text-slate-400 shrink-0 whitespace-nowrap" title={fullDate}>
            {activityRelativeTime(entry.createdAt, tz)}
          </span>
        </div>
        {fullDate && (
          <p className="mt-0.5 text-[11px] text-slate-400">{fullDate}</p>
        )}
        {messageRendered && (
          <div
            className={`mt-1 prose prose-sm max-w-none text-slate-600 ${!expanded && isLong ? 'line-clamp-2' : ''}`}
            dangerouslySetInnerHTML={{ __html: messageRendered }}
          />
        )}
        {isLong && (
          <button
            onClick={() => setExpanded(v => !v)}
            className="mt-0.5 flex items-center gap-0.5 text-[11px] text-indigo-600 hover:text-indigo-800"
          >
            {expanded ? <><ChevronUp className="h-3 w-3" /> Less</> : <><ChevronDown className="h-3 w-3" /> More</>}
          </button>
        )}
        <div className="mt-1 flex items-center gap-2 text-[11px] text-slate-400">
          {authorName && <span>{authorName}</span>}
          {entry.deliveryStatus && (
            <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{entry.deliveryStatus}</span>
          )}
        </div>
      </div>
    </div>
  );
}

function HistoryEntryCard({ entry, tz }: { entry: PlanHistoryEntry; tz: string }) {
  const dateStr = formatActivityFullDate(entry.date, tz);

  return (
    <div className="flex gap-3 p-3 bg-white border border-slate-200 rounded-xl">
      <div className="flex-shrink-0 mt-0.5 p-1.5 rounded-lg text-violet-600 bg-violet-50">
        <History className="h-4 w-4 shrink-0" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 mb-2">
          <span className="text-sm font-bold text-slate-800">{dateStr}</span>
          {entry.userName && <span className="text-[11px] text-slate-400">by {entry.userName}</span>}
        </div>
        <table className="text-sm">
          <tbody>
            {entry.changes.map((c, i) => (
              <tr key={i}>
                <td className="pr-4 py-0.5 font-medium text-slate-600 whitespace-nowrap align-top w-px">{c.field}</td>
                <td className="pr-4 py-0.5 text-slate-400 whitespace-nowrap align-top w-px">{c.verb}</td>
                <td className="py-0.5 text-slate-900 align-top">{c.newValue || '(empty)'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PlanActivitySection({
  planId,
  tz,
}: {
  planId: number;
  tz: string;
}) {
  const [collapsed, setCollapsed] = useState(true);
  const [activeTab, setActiveTab] = useState<ActivityTabKey>('notes');
  const tabRowRef = useRef<HTMLDivElement>(null);
  const tabMeasureRef = useRef<HTMLDivElement>(null);
  const [useTabDropdown, setUseTabDropdown] = useState(false);

  useEffect(() => {
    const row = tabRowRef.current;
    const measure = tabMeasureRef.current;
    if (!row || !measure) return;
    const check = () => {
      setUseTabDropdown(measure.scrollWidth > row.clientWidth - 2);
    };
    check();
    const ro = new ResizeObserver(check);
    ro.observe(row);
    return () => ro.disconnect();
  }, [collapsed]);

  const { data: allEntries = [], isLoading: commLoading } = useQuery({
    queryKey: ['plan-activity-commlog', planId],
    queryFn: async () => {
      let all: CommLogEntry[] = [];
      let page = 1;
      let hasMore = true;
      while (hasMore) {
        const res = await getCommLogByPlan(planId, page);
        all = [...all, ...res.data];
        hasMore = res.meta?.hasNext ?? (res.data.length > 0 && all.length < (res.meta?.total ?? 0));
        page++;
        if (page > 20) break;
      }
      return all;
    },
    enabled: planId > 0,
    staleTime: 60_000,
  });

  const { data: historyEntries = [], isLoading: historyLoading, isError: historyError, error: historyErrorObj } = useQuery({
    queryKey: ['plan-history', planId],
    queryFn: () => getPlanHistory(planId),
    enabled: planId > 0 && !collapsed && activeTab === 'history',
    staleTime: 60_000,
    retry: 1,
  });

  const commEntries = useMemo(
    () => allEntries.filter(e => !COMM_EXCLUDE_TYPES.includes(e.type)),
    [allEntries],
  );
  const stageEntries = useMemo(
    () => allEntries.filter(e => e.type === 'stage_change'),
    [allEntries],
  );
  const noteEntries = useMemo(
    () => allEntries.filter(e => e.type === 'note'),
    [allEntries],
  );

  const totalComms = allEntries.length;

  const activeEntries =
    activeTab === 'comms' ? commEntries
      : activeTab === 'stages' ? stageEntries
        : activeTab === 'notes' ? noteEntries
          : [];

  const isLoading = activeTab === 'history' ? historyLoading : commLoading;

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <button
        type="button"
        className="flex w-full items-center gap-2 px-4 py-2.5 text-left hover:bg-slate-50 transition-colors rounded-t-xl"
        onClick={() => setCollapsed(c => !c)}
      >
        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${collapsed ? '-rotate-90' : ''}`} />
        <ScrollText className="h-4 w-4 text-indigo-500" />
        <h3 className="text-sm font-semibold text-slate-700">Activity</h3>
        {totalComms > 0 && !collapsed ? null : totalComms > 0 && (
          <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-slate-200 px-1.5 text-[10px] font-bold text-slate-600 leading-none">
            {totalComms}
          </span>
        )}
      </button>

      {!collapsed && (
        <div className="space-y-3 px-4 pb-4">
          {/* Hidden measure strip */}
          <div ref={tabMeasureRef} aria-hidden className="pointer-events-none invisible absolute flex items-center gap-1 whitespace-nowrap" style={{ height: 0, overflow: 'hidden' }}>
            {ACTIVITY_TABS.map(tab => (
              <span key={tab.key} className="inline-flex shrink-0 items-center px-3 py-1.5 text-xs font-medium">{tab.label}</span>
            ))}
          </div>

          {/* Tabs */}
          <div ref={tabRowRef} className="flex items-center gap-2 border-b border-slate-200 pb-1">
            {useTabDropdown ? (
              <select
                value={activeTab}
                onChange={e => setActiveTab(e.target.value as ActivityTabKey)}
                className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-600 focus:outline-none focus:ring-1 focus:ring-primary"
              >
                {ACTIVITY_TABS.map(tab => (
                  <option key={tab.key} value={tab.key}>{tab.label}</option>
                ))}
              </select>
            ) : (
              // SCRUM-1389. The row had no horizontal escape, so the fourth tab
              // ("History") and its badge were clipped by the card edge and the whole
              // page scrolled sideways to compensate. Below `md` the strip scrolls in
              // place instead — the same `overflow-x-auto scrollbar-hide` + `shrink-0`
              // idiom DetailSubNav uses for the patient section strip.
              <div className="flex items-center gap-1 min-w-0 max-md:overflow-x-auto max-md:scrollbar-hide">
                {ACTIVITY_TABS.map(tab => {
                  const isActive = activeTab === tab.key;
                  const count = tab.key === 'comms' ? commEntries.length
                    : tab.key === 'stages' ? stageEntries.length
                      : tab.key === 'notes' ? noteEntries.length
                        : historyEntries.length;
                  return (
                    <button
                      key={tab.key}
                      type="button"
                      onClick={() => setActiveTab(tab.key)}
                      // The selected tab carries `border-b-2` and the others carry no
                      // bottom border at all, so the active one is 2px taller and its
                      // grey box rides above the row — the "sits higher / looks broken"
                      // in SCRUM-1389. A transparent border of the same width squares
                      // them up. Scoped `max-md:` so the desktop row keeps the geometry
                      // it has today; DetailSubNav uses this idiom unscoped.
                      className={`inline-flex shrink-0 items-center gap-1.5 rounded-t-md px-3 py-1.5 text-xs font-medium transition-colors ${
                        isActive
                          ? 'border-b-2 border-primary text-primary bg-primary/10'
                          : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50 max-md:border-b-2 max-md:border-transparent'
                      }`}
                    >
                      {tab.label}
                      {count > 0 && (
                        <span className={`inline-flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-[10px] font-bold leading-none ${
                          isActive ? 'bg-primary/15 text-primary' : 'bg-amber-100 text-amber-600'
                        }`}>
                          {count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Content — fixed height container to prevent layout jump on tab switch */}
          <div className="overflow-auto" style={{ height: 300 }}>
            {isLoading ? (
              <div className="flex justify-center py-6"><Spinner /></div>
            ) : activeTab === 'history' && historyError ? (
              <p className="py-4 text-sm text-red-500 italic">
                Failed to load history{historyErrorObj instanceof Error ? `: ${historyErrorObj.message}` : '.'}
              </p>
            ) : activeTab === 'history' ? (
              historyEntries.length === 0 ? (
                <p className="py-4 text-sm text-slate-400 italic">No history entries.</p>
              ) : (
                <div className="space-y-2">
                  {[...historyEntries].reverse().map((entry, i) => (
                    <HistoryEntryCard key={i} entry={entry} tz={tz} />
                  ))}
                </div>
              )
            ) : activeEntries.length === 0 ? (
              <p className="py-4 text-sm text-slate-400 italic">No entries found.</p>
            ) : (
              <div className="space-y-2">
                {activeEntries.map(entry => (
                  <CommEntryCard key={entry.commLogId} entry={entry} tz={tz} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default PlansTab;
